import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Megaphone, AlertTriangle, ShieldAlert, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Per-severity rendering recipe. Keeps the JSX below readable and makes
// adding a new severity (e.g. ``success``) a one-line change.
const SEVERITY_STYLES = {
  info: {
    icon: Megaphone,
    container:
      'border-amber-300 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50',
    iconColor: 'text-amber-600',
    textColor: 'text-amber-900',
    metaColor: 'text-amber-700/70',
    dismissColor: 'text-amber-700/60 hover:text-amber-900',
    pulse: false,
  },
  warning: {
    icon: AlertTriangle,
    container:
      'border-orange-400 bg-gradient-to-r from-orange-50 via-red-50 to-orange-50',
    iconColor: 'text-orange-600',
    textColor: 'text-orange-900',
    metaColor: 'text-orange-700/70',
    dismissColor: 'text-orange-700/60 hover:text-orange-900',
    pulse: false,
  },
  alert: {
    icon: ShieldAlert,
    container:
      'border-red-500 bg-gradient-to-r from-red-50 via-rose-50 to-red-50 ring-2 ring-red-200',
    iconColor: 'text-red-600',
    textColor: 'text-red-900 font-medium',
    metaColor: 'text-red-700/70',
    dismissColor: 'text-red-700/60 hover:text-red-900',
    pulse: true,
  },
};

/**
 * HomeBanner — Floating announcement banner shown ONLY on the Employee Home /
 * Dashboard page, between the global header and the "Welcome back, ..." card.
 *
 * Polls ``GET /api/banners/active`` once on mount + on tab focus, and renders
 * each currently-visible banner stacked. Auto-hides itself if no admin has
 * created an active banner for today.
 *
 * Banners are dismissable per-tab (the close button hides them via local
 * state) but they re-appear on next page load — by design, so admins can be
 * confident every employee sees important announcements.
 */
const HomeBanner = () => {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState({});

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/banners/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // Silent — a missing-banner endpoint should never block the dashboard.
    }
  };

  useEffect(() => {
    fetchBanners();
    const onVis = () => { if (document.visibilityState === 'visible') fetchBanners(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const visible = banners.filter(b => !dismissed[b.id]);
  if (visible.length === 0) return null;

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 max-w-screen-2xl mx-auto w-full" data-testid="home-banner-container">
      {visible.map((b) => {
        const sev = SEVERITY_STYLES[b.severity] || SEVERITY_STYLES.info;
        const Icon = sev.icon;
        return (
          <div
            key={b.id}
            data-testid={`home-banner-${b.id}`}
            data-severity={b.severity || 'info'}
            className={`mb-3 flex items-start gap-3 rounded-lg border px-4 py-3 shadow-sm animate-[fadeIn_0.3s_ease-out] ${sev.container} ${sev.pulse ? 'animate-pulse' : ''}`}
          >
            <Icon className={`w-5 h-5 ${sev.iconColor} mt-0.5 flex-shrink-0`} />
            <div className={`flex-1 text-sm leading-relaxed ${sev.textColor}`}>
              {b.message}
              <span className={`hidden sm:inline ml-2 text-xs ${sev.metaColor}`}>
                ({b.start_date} {b.start_time || '00:00'} → {b.end_date} {b.end_time || '23:59'} IST)
              </span>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(d => ({ ...d, [b.id]: true }))}
              className={`transition-colors ${sev.dismissColor}`}
              aria-label="Dismiss banner"
              data-testid={`home-banner-dismiss-${b.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default HomeBanner;
